/**
 * Shared consent-state resolution. Used by both:
 *   - `Recorder.getConsentState()` — gating the actual recording start
 *   - top-level `getConsent()` — public API surface for host pages
 *
 * Having one source of truth means a host page checking `getConsent()`
 * before deciding to call `startRecording()` sees the same state the
 * recorder will apply — DNT/GPC opt-outs are honoured equally, instead
 * of the public API silently reporting `'pending'` while the recorder
 * treats the visitor as `'denied'`.
 */

import type { ConsentState } from '@/types';

export const CONSENT_KEY = 'cobrowsing_consent';

/**
 * Resolution order:
 *   1. Explicit stored decision (`'granted'` / `'denied'`) — always wins.
 *   2. Opt-out signals in priority order (first match → `'denied'`):
 *        `window.__chatneerOptOut === true`,
 *        `<html data-chatneer-no-record>`,
 *        `navigator.doNotTrack === '1'`,
 *        `navigator.globalPrivacyControl === true`.
 *   3. `autoGrantConsent` (default `true`) — transient `'granted'`, never persisted.
 *   4. Otherwise `'pending'` — caller must explicitly grant.
 */
export function resolveConsentState(
  opts: { autoGrantConsent?: boolean | undefined } = {}
): ConsentState {
  try {
    const stored = sessionStorage.getItem(CONSENT_KEY);
    if (stored === 'granted' || stored === 'denied') return stored;
  } catch {
    // Private browsing — fall through.
  }

  if (hasOptOutSignal()) return 'denied';
  if (opts.autoGrantConsent !== false) return 'granted';
  return 'pending';
}

/** Defensive against missing globals (SSR / Node test envs). */
export function hasOptOutSignal(): boolean {
  if (typeof window !== 'undefined') {
    const winFlag = (window as unknown as { __chatneerOptOut?: unknown }).__chatneerOptOut;
    if (winFlag === true) return true;
    if (typeof document !== 'undefined' && document.documentElement) {
      if ('chatneerNoRecord' in document.documentElement.dataset) return true;
    }
  }
  if (typeof navigator !== 'undefined') {
    if (navigator.doNotTrack === '1') return true;
    const gpc = (navigator as unknown as { globalPrivacyControl?: unknown }).globalPrivacyControl;
    if (gpc === true) return true;
  }
  return false;
}
